import * as Commons from "oc-commons/Commons";
import {
  filterPathIntersectionCached,
  findPathIntersectionCached,
  GraphMetadata,
} from "oc-app/metadata/GraphMetadata";
import * as Maths3 from "oc-threejs/Maths3";
import { v3 } from "oc-threejs/short3";
import { AngleInterval, parseAngleIntervalDefinition } from "./commons/AngleIntervals";
import {
  MArtikel,
  MAuswahlwert,
  MEckadapter,
  MElement,
  MElementhalter,
  Metamodell,
  MIllegaleKombinationen,
  MModel,
  MModelMaterialRelation,
  MObjekt,
  MPfosten,
  MTor,
  MUMProfil,
  MWarnung_Dialog,
} from "../metamodell";
import { PAuswahlwert, PZubehoer } from "../metamodell_paths";

export function checkMMR(mmr: any) {
  if (mmr == null) {
    return;
  }
  if (
    mmr.id == null ||
    mmr.Objekt == null ||
    mmr.Model == null ||
    !_.isPlainObject(mmr.Json ?? {}) ||
    !_.isPlainObject(mmr.MaterialMap ?? {})
  ) {
    debugger;
    throw new Error("not an ModelMaterialRelation");
  }
  return mmr;
}

export function checkMMRNotUndefined(mmr: any) {
  if (mmr === null) {
    return;
  }
  if (mmr === undefined) {
    debugger;
    throw new Error("ModelMaterialRelation is undefined");
  }
  return checkMMR(mmr);
}

export function getMMR(meta: any): MModelMaterialRelation {
  if (meta == null) {
    return null;
  }
  const mmrs = meta.ModelMaterialRelation;
  if (mmrs?.length != 1) {
    debugger;
    throw new Error("must be exactly 1 ModelMaterialRelation for " + meta?.Key);
  }
  const mmr = mmrs[0];
  return checkMMRNotUndefined(mmr);
}

export const findMMR = (objectKey: string) => {
  const mmr = APP.richMetadata.findModelMaterialRelationForKey(objectKey);
  if (!mmr && objectKey != null) {
    console.warn("ModelMaterialRelation with Objekt.Key", objectKey, "not found");
  }
  return mmr;
};

export const istUnecht = (artikel: MArtikel) => Commons.isEmpty(artikel.Artikelnummer);

declare module "../metamodell" {
  interface MElement {
    Profilleiste: any;
    Steckprofile_Kombinationen: any;
  }
  interface MPfosten {
    WinkelbereicheParsed: AngleInterval[];
    RotationYParsed: number;
  }
}

export interface UMProfilMetaParamter {
  pfostenfarbe: MObjekt;
  pfostentyp: MObjekt;
  mauer?: boolean;
  gesuchtePfostenHöhe: number;
  aufbautyp: MObjekt;
  toraufhängung?: boolean;
  torschloss?: boolean;
  anschlagprofilAuswahlwert?: MAuswahlwert;
  anschlagprofilfarbeAuswahlwert?: MAuswahlwert;
  pfostenartAuswahlwert?: MAuswahlwert;
  isElementhalter?: boolean;
  isEckadapter?: boolean;
  isWinkelbereichU?: boolean;
}

/**
 * Das RichMetadata dient dazu, die metadata, also das Produktmodell, aufzubereiten.
 */
export class RichMetadata extends GraphMetadata {
  metadata: Metamodell;
  modelMaterialRelationMap: Record<string, MModelMaterialRelation>;
  missingAuswahlwertKeys: Set<string> = new Set();

  clearMissingAuswahlwertKeys() {
    this.missingAuswahlwertKeys.clear();
  }

  async init() {
    if (!APP.isTorkonfigurator) this.setObjektEasyAuswahlwertInAuswahlwert();
    await super.init();
    this.fillModelMateralRelationMap();
    // this._resolveBoundingBoxesAndRenderPromise();
    this._addToreZuElementen();
    this._modifyModelDatas();
    this._initWinkelbereicheParsed();
    this._ensurePotentiellUnechteArtikel();
  }

  setObjektEasyAuswahlwertInAuswahlwert() {
    [this.metadata.Objekt, this.metadata.DetailSerie].forEach((table) =>
      table
        .filter((o) => !Commons.isEmpty(o.EasyAuswahlwert))
        .forEach((o) => (o.Auswahlwert = o.EasyAuswahlwert))
    );
  }

  getAuswahlWertVoreinstellungForKey = Commons.cache((key: string) => {
    const auswahl = this.metadata.Auswahl.find((a) => a.Key == key);
    if (auswahl == null) {
      console.error(`Auswahl mit Key="${key}" fehlt in der Datenbank!`);
    }
    return this.getAuswahlWertVoreinstellung(auswahl?.Auswahlwert?.toReversed(), {
      allowNull: true,
    });
  });

  getAuswahlWertVoreinstellungOrFirstForKey = Commons.cache((key: string) => {
    const auswahl = this.metadata.Auswahl.find((a) => a.Key == key);
    if (auswahl == null) {
      console.error(`Auswahl mit Key="${key}" fehlt in der Datenbank!`);
    }
    return this.getAuswahlWertVoreinstellung(auswahl?.Auswahlwert?.toReversed(), {
      allowNull: false,
    });
  });

  getZubehöre(zubehörType: string, zubehörAnzahlen: Record<string, number>) {
    const zubehörEntries = APP.metadata.Zubehoer.filter((meta) => meta.Type == zubehörType);
    return Object.entries(zubehörAnzahlen)
      .filter(([key, anzahl]) => anzahl > 0)
      .map(([key, anzahl]) => ({
        zubehör: zubehörEntries.find(({ Auswahlwert }) => Auswahlwert.Key == key),
        anzahl,
      }));
  }

  getDefaultAnzahlForEAntriebZubehör = () => {
    return this.getDefaultAnzahlForZubehörByType("E_Antrieb_Zubehör");
  };

  getDefaultAnzahlForZubehörByType = (type: string) => {
    const zubehörAuswahlwerte = APP.metadata.Auswahlwert.filter((w) => w.Auswahl.Key == type);
    const defaultAnzahl = {};
    zubehörAuswahlwerte.forEach((auswahlwert) => {
      const anzahl = findPathIntersectionCached(`RichMetadata:defaultAnzahl:${type}`)(
        auswahlwert,
        (a: PAuswahlwert) => a.Zubehoer.DefaultAnzahl
      ) as number;
      defaultAnzahl[auswahlwert.Key] = anzahl;
    });
    return defaultAnzahl;
  };

  getAuswahlWertVoreinstellung(
    auswahlwerte: MAuswahlwert[] = [],
    { allowNull } = { allowNull: false }
  ) {
    return auswahlwerte.find((a) => a.Voreinstellung) ?? (allowNull ? null : _.first(auswahlwerte));
  }

  ensureAuswahlWert(
    auswahlwerte: MAuswahlwert[] = [],
    auswahlwert: MAuswahlwert,
    { allowNull } = { allowNull: false }
  ) {
    if (auswahlwerte.includes(auswahlwert)) {
      return auswahlwert;
    }
    return this.getAuswahlWertVoreinstellung(auswahlwerte, { allowNull });
  }

  getObjektTyp(objekt: any) {
    if (objekt == null) {
      return null;
    }
    return objekt.Objekttyp ?? objekt.Typ ?? this.rowToMetatableMap.get(objekt)?.name;
  }

  // Auskommentierter Code entfernt - verwendete veraltetes adjacency-System
  // Falls Model-Bounding-Boxes benötigt werden, über explizite Backreferenzen implementieren

  fillModelMateralRelationMap() {
    this.modelMaterialRelationMap = {};
    this.metadata.ModelMaterialRelation.forEach((row, index: number) => {
      this.modelMaterialRelationMap[row.Objekt.Key] = row;
    });
  }

  _addToreZuElementen() {
    this.metadata.Element = this.metadata.Element.concat(this.metadata.Tor);

    // Liste der Properties, die KEINE Backreferenzen sind
    const ignoredProperties = new Set([
      "id",
      "Key",
      "created_at",
      "updated_at",
      "ignored",
      // Forward-References (Arrays, aber keine Backreferenzen):
      "Anschlag",
      "Beschlagsatz",
      "Drücker",
      "Einsteckschloss",
      "Gehflügel",
      "Öffnung",
      "Profilzylinder",
      "Toröffner",
      "Torschließer",
      // Von Element geerbt:
      "Objekt",
      "Auswahlwert",
      "EasyAuswahlwert",
      "Form",
      "Füllung",
      "Profilleiste",
      "Aufsatzleiste",
      "Pfosten_links",
      "Pfosten_rechts",
    ]);

    this.metadata.Tor.forEach((tor) => {
      // Iteriere über alle Properties des Tors
      Object.keys(tor).forEach((propertyName) => {
        // Überspringe ignorierte Properties
        if (ignoredProperties.has(propertyName)) {
          return;
        }

        const value = tor[propertyName];

        // Nur Arrays sind Backreferenzen (außer Forward-References, die wir bereits ignoriert haben)
        if (Array.isArray(value)) {
          value.forEach((related: any) => {
            if (related && typeof related === "object") {
              // Füge Tor zur Element-Backreference des related-Objekts hinzu
              if (!related.Element) {
                related.Element = [];
              }
              if (!Array.isArray(related.Element)) {
                related.Element = [related.Element];
              }
              related.Element.push(tor);
            }
          });
        }
      });
    });
  }

  _modifyModelDatas() {
    this.metadata.Model.forEach((model) => this._modifyModelData(model));
  }

  _modifyModelData(model: MModel) {
    const modelData = model.ModelData ?? {};
    const defaultModelData = this.getDefaultModelData(model);
    Object.assign(modelData, {
      ...defaultModelData,
      ...modelData,
    });
  }

  getDefaultModelData(model: MModel) {
    const metaObjekt = model.ModelMaterialRelation?.[0]?.Objekt;
    const objekttyp = metaObjekt && this.getObjektTyp(metaObjekt);
    const defaultModelData = {
      Rotation: Maths3.quaternion.zero.clone(),
      Scale: Maths3.vector.one.clone(),
      Position: Maths3.vector.zero.clone(),
      AutoCenter: ![
        "Kloben",
        "Schanier",
        "Schlossanschlag",
        "Schlossstulp",
        "Druecker",
        "Bodenbefestigung",
        "Montageplatten",
      ].includes(objekttyp),
      GridPosition: ["Rahmen", "Beschlagsatz", "Querleiste"].includes(objekttyp)
        ? v3(0.5, 0.5, 0.5)
        : [
              "Pfosten",
              "U-Klemmprofil",
              "U-Montageprofil",
              "Anschlagprofil",
              "Eckadapter",
              "Ummantelung",
            ].includes(objekttyp)
          ? v3(0.5, 0, 0.5)
          : v3(0, 0, 0.5),
      MaterialMap: null,
      ObjektTyp: objekttyp,
    };
    return defaultModelData;
  }

  _initWinkelbereicheParsed() {
    this.metadata.Pfosten.forEach((p) => {
      p.WinkelbereicheParsed = parseAngleIntervalDefinition(p.Winkelbereiche ?? "0°, 180°");
      p.RotationYParsed = Commons.isEmpty(p.RotationY)
        ? 0
        : (parseFloat(p.RotationY) * 180) / Math.PI;
    });
  }

  ensureKeyGetter(getter: Function) {
    return Commons.cacheIdentity((key: string) => {
      const found = getter(key);
      if (!found && key) {
        throw new Error(key + " not found");
      }
      return found;
    });
  }

  findAuswahlwertOrDefault = (auswahlwertKey: string, auswahlKey: string) => {
    const found = this.findAuswahlwert(auswahlwertKey);
    if (!found && !Commons.isEmpty(auswahlwertKey)) {
      this.missingAuswahlwertKeys.add(auswahlwertKey);
      console.warn(
        `Auswahlwert mit Key="${auswahlwertKey}" nicht gefunden, verwende Voreinstellung für "${auswahlKey}"`
      );
    }
    return found ?? this.getAuswahlWertVoreinstellungForKey(auswahlKey);
  };

  findAuswahlwertOrDefaultOrFirst = (auswahlwertKey: string, auswahlKey: string) => {
    const found = this.findAuswahlwert(auswahlwertKey);
    if (!found && !Commons.isEmpty(auswahlwertKey)) {
      this.missingAuswahlwertKeys.add(auswahlwertKey);
      console.warn(
        `Auswahlwert mit Key="${auswahlwertKey}" nicht gefunden, verwende Voreinstellung für "${auswahlKey}"`
      );
    }
    return found ?? this.getAuswahlWertVoreinstellungOrFirstForKey(auswahlKey);
  };

  findEnsureAuswahlwert = this.ensureKeyGetter((key: string) => this.findAuswahlwert(key));

  findEnsureAnyObjekt = this.ensureKeyGetter((key: string) => this.findAnyObjekt(key));

  findAnyObjekt = (key: string): any =>
    this.findObjekt(key) ||
    this.findElement(key) ||
    this.findTor(key) ||
    this.findPfosten(key) ||
    this.findUMProfil(key) ||
    this.findMass(key) ||
    this.findPfostenmass(key) ||
    this.findProfilkategorie(key) ||
    this.findSerie(key) ||
    this.findAufbauten(key) ||
    this.findEckadapter(key) ||
    this.metadata.Inspiration.find((x) => x.Inspiration.Key == key);

  findObjektForTypeAndAuswahlwert = Commons.cache(
    (objekttypKey: string, auswahlwertKey: string) => {
      const objekts = this.metadata.Objekt.filter((o) => o.Objekttyp == objekttypKey);
      return objekts.find((o) => o.Auswahlwert?.Key == auswahlwertKey) ?? objekts[0];
    }
  );

  findInspiration = (key) =>
    APP.metadata.Inspiration.find((inspiration) => inspiration?.Inspiration?.Key == key);

  getWarnungDialogByKey = Commons.cache((key: string): MWarnung_Dialog | null => {
    if (Commons.isEmpty(key)) {
      return null;
    }
    return this.metadata?.Warnung_Dialog?.find((entry) => entry?.Key == key) ?? null;
  });

  getDummyPfosten = Commons.cache(() => APP.metadata.Pfosten.find((p) => p.Key.startsWith("2341")));

  getDeltaabstandsmassRow(
    elementMeta?: MElement,
    pfostenMeta?: MPfosten,
    elementhalterMeta?: MElementhalter,
    umprofilMeta?: MUMProfil,
    eckadapterMeta?: MEckadapter
  ) {
    const equalsMeta = (a, b) => (a?.Key === undefined ? a === b : a.Key === b?.Key);
    const pointsIf = (a, b, points) =>
      Commons.isEmpty(a)
        ? 0
        : Array.isArray(a)
          ? a.some((t) => equalsMeta(t, b))
          : equalsMeta(a, b)
            ? points
            : -100;
    const scoreMap = (epa) => {
      let score = 1;
      score += pointsIf(epa.Form, elementMeta?.Mass?.Form, 2);
      score += pointsIf(epa.DetailSerie, elementMeta?.DetailSerie[0], 1);
      score += pointsIf(epa.Element, elementMeta, 10);
      score += pointsIf(epa.Pfostentyp, pfostenMeta?.Pfostentyp, 1);
      score += pointsIf(epa.Pfosten, pfostenMeta, 10);
      score += pointsIf(epa.Elementhalter, elementhalterMeta, 1);
      score += pointsIf(epa.UMProfil, umprofilMeta, 1); //remove after Datamigration
      score += pointsIf(epa.UMProfilTyp, umprofilMeta?.Typ, 1);
      score += pointsIf(epa.Eckadapter, eckadapterMeta, 1);
      return score;
    };

    const bestFittingAbstandsmass = _.maxBy(this.metadata.Element_Pfosten_Abstandsmass, scoreMap);

    // const map = this.metadata.Element_Pfosten_Abstandsmass.map((epa) => ({
    //   epa,
    //   score: scoreMap(epa),
    // }));
    // const sorted = map.toSorted((a, b) => a.score - b.score);
    // console.log({
    //   bestFittingAbstandsmass,
    //   sorted,
    //   elementMeta,
    //   pfostenMeta,
    //   elementhalterMeta,
    //   umprofilMeta,
    //   eckadapterMeta,
    // });

    return bestFittingAbstandsmass;
  }

  getDeltaabstandsmass(
    elementMeta: MElement,
    pfostenMeta: MPfosten,
    elementhalterMeta: MElementhalter,
    umprofilMeta: MUMProfil,
    eckadapterMeta?: MEckadapter
  ) {
    const row = this.getDeltaabstandsmassRow(
      elementMeta,
      pfostenMeta,
      elementhalterMeta,
      umprofilMeta,
      eckadapterMeta
    );
    return row?.Deltaabstandsmass ?? 0;
  }

  findModelMaterialRelationForKey = Commons.cache((key: string) =>
    APP.metadata.ModelMaterialRelation.find((mr) => mr.Objekt.Key == key)
  );

  findPfostentypForPfostenartAuswahl = Commons.cacheIdentity(
    (pfostenartAuswahlwert: MAuswahlwert) =>
      filterPathIntersectionCached("RichMetadata.js:337")(
        pfostenartAuswahlwert,
        (x: PAuswahlwert) => x.Pfostenart_Objekt.Pfosten.Pfostentyp
      )[0]
  );

  getUMProfilMeta({
    pfostenfarbe,
    pfostentyp,
    mauer = false,
    gesuchtePfostenHöhe,
    aufbautyp,
    toraufhängung = false,
    torschloss = false,
    anschlagprofilAuswahlwert = null,
    anschlagprofilfarbeAuswahlwert = null,
    // pfostenartAuswahlwert muss null sein, wenn sich um einen Torpfosten und die Torseite handelt.
    // Wenn es sich um einen Torpfosten und ein normales Element handelt, muss es Steckpfosten oder Klemmpfosten sein.
    pfostenartAuswahlwert = null,
    isElementhalter = false,
    isEckadapter = false,
    isWinkelbereichU = false,
  }: UMProfilMetaParamter) {
    if (isElementhalter) {
      return null;
    }

    const rules = this.getUMProfilMetaSortedDesc();
    const filteredRules = rules.filter(
      (rule) =>
        rule.MinMaxY.x <= gesuchtePfostenHöhe &&
        gesuchtePfostenHöhe < rule.MinMaxY.y &&
        (Commons.isEmpty(rule.Aufbautyp) || rule.Aufbautyp.includes(aufbautyp)) &&
        rule.Toraufhängung == toraufhängung &&
        rule.Torschloss == torschloss &&
        rule.Mauer == mauer &&
        rule.Eckadapter == isEckadapter &&
        rule.Winkelbereich_U == isWinkelbereichU &&
        (Commons.isEmpty(rule.Pfostentyp) || rule.Pfostentyp?.includes(pfostentyp)) &&
        (toraufhängung ||
          torschloss ||
          Commons.isEmpty(rule.AuswahlwertPfostenart) ||
          rule.AuswahlwertPfostenart === pfostenartAuswahlwert)
    );
    // if (filteredRules.length > 1) {
    //   console.warn("UM profil Kombination nicht eindeutig.", filteredRules);
    // }
    for (const rule of filteredRules) {
      const umprofil =
        rule.UMProfile.find(
          (umprofil) =>
            (!Commons.isEmpty(umprofil.MontageplattenFarbe) &&
              umprofil.MontageplattenFarbe.Auswahlwert == anschlagprofilfarbeAuswahlwert) ||
            ((Commons.isEmpty(umprofil.Pfostenfarbe) || umprofil.Pfostenfarbe == pfostenfarbe) &&
              (Commons.isEmpty(umprofil.Auswahlwert) ||
                umprofil.Auswahlwert == anschlagprofilAuswahlwert))
        ) ?? _.first(rule.UMProfile);

      if (umprofil) {
        // console.log("RULE", rule, umprofil);
        return umprofil;
      }
    }
    return null;
  }

  getUMProfilMetaSortedDesc = Commons.cache(() =>
    [...APP.metadata.UMProfil_Kombinationen].sort((a, b) => b.Sortierung - a.Sortierung)
  );

  /**
   * Checks if Kombination of metas is illegal and returns the reasons why it is
   */
  isInvalidMetaCombination = Commons.cacheFactory({
    valueFactory: (
      meta1: MElement | MTor | MPfosten,
      meta2: MElement | MTor | MPfosten
    ): MIllegaleKombinationen[] => {
      return this.metadata.IllegaleKombinationen.filter(
        (illegal) =>
          (illegal.Objekt1.includes(meta1) && illegal.Objekt2.includes(meta2)) ||
          (illegal.Objekt1.includes(meta2) && illegal.Objekt2.includes(meta1))
      );
    },
    keyMapper: (meta1: MElement | MTor | MPfosten, meta2: MElement | MTor | MPfosten) =>
      meta1?.Key < meta2?.Key ? `${meta1?.Key}--${meta2?.Key}` : `${meta2?.Key}--${meta1?.Key}`,
  });

  /**
   */
  getCookieData = Commons.cache(() => {
    return APP.metadata.Cookies.map(
      // @ts-ignore
      ({
        key,
        label,
        category,
      }: {
        key: string;
        label: string;
        category: "required" | "comfort" | "advertising" | "statistic";
      }) => {
        return { key, label, category };
      }
    );
  });

  getEAntriebInfo(part: any) {
    const pfostenfarbeAuswahlwert =
      part.pfostenLinks?.pfostenfarbeAuswahlwert ?? part.pfostenRechts?.pfostenfarbeAuswahlwert;
    if (pfostenfarbeAuswahlwert == null) {
      return null;
    }
    const antrieb = this.metadata.Farbmapping.find(
      (antrieb) =>
        antrieb.Auswahlwert.Auswahl?.Key === "E_Antrieb_Farbe" &&
        (antrieb.PassendePfostenfarbe.map((f) => f.Auswahlwert).some(
          (a) => a.Key == pfostenfarbeAuswahlwert.Key
        ) ||
          antrieb.SonstigePfostenfarbe.map((f) => f.Auswahlwert).some(
            (a) => a.Key == pfostenfarbeAuswahlwert.Key
          ))
    );
    return {
      antrieb,
      hinweis: antrieb?.PassendePfostenfarbe.map((f) => f.Auswahlwert).some(
        (a) => a.Key === pfostenfarbeAuswahlwert.Key
      )
        ? ""
        : antrieb.Auswahlwert.Hinweistext,
    };
  }

  /**
   * sammle alle in der Gruppe vorhandenen klammerObjekte ein und ersetze den Artikel durch eine
   * virtuelle Instanz, welche die aufgesammelten klammerArtikel referenziert
   * Klammer-Artikel dienen dazu, die Anzahl der Einträge in der Artikel-Tabelle klein zu halten
   * für den Fall, dass ein Kreuzprodukt von Objekten zu gleichartigen Artikeln gemappt werden muss. Beispiel sind
   * Dekorprofile und Dekorprofiladapter, welche in allen Kombinationen auftreten können und streng genommen in jeder
   * Kombination auf einen eigenen Artikel gemappt werden müssten.
   * Klammerartikel wirken nur innerhalb einer Baugruppe. Aller Artikel derselben Klammer werden zu einem einzigen
   * virtuellen Artikel zusammengefasst, der aus der Kombination aller Klammerartikel besteht. In der weitere Verarbeitung
   * werden die Klammerartikel also als Einheit betrachtet. In der Ausgabe der Preis- oder Baugruppen führt die Klammer dazu,
   * dass dieser virtuelle Artikel zusammen mit seinen KlammerArtikeln direkt aufeinander folgend ausgegeben wird.
   */
  _ensurePotentiellUnechteArtikel() {
    const klammernMap = Commons.groupBy(
      this.metadata.Artikel.filter((x) => x.Klammer),
      (x) => x.Klammer
    );
    klammernMap.forEach((mArtikels, klammer) => {
      const [unechteArtikel, echteArtikel] = Commons.partition(mArtikels, istUnecht);
      echteArtikel.forEach((a) => (a.potentiellUnechteArtikel = unechteArtikel));
    });
  }

  findForm = Commons.cache((key: string) => this.metadata.Form_Objekt.find((f) => f.Key == key));
}

//
// aufbautyp
//     {id: 189, Objekttyp: 'Aufbautyp', Key: 'Platten', Auswahlwert: null, EasyAuswahlwert: null, …}
//
// gesuchtePfostenHöhe
//     1.9400000000000002
//
// mauer
//     true
//
// pfostenfarbe
//     {id: 69, Objekttyp: 'Pfostenfarbe', Key: 'Anthrazit', Auswahlwert: {…}, EasyAuswahlwert: null, …}
//
// pfostentyp
//     {id: 12, Objekttyp: 'Pfostentyp', Key: 'SYSTEM Klemmpfosten', Auswahlwert: {…}, EasyAuswahlwert: null, …}
//
// toranschlag
//     true
//
// torkloben
//     false
